import { defineStore } from 'pinia'
import { ref } from 'vue'
import { AsYouType } from 'libphonenumber-js'

const STORE_NAME = 'contacts'
const LOCALSTORAGE_KEY = 'contacts'

export const useContactsStore = defineStore(STORE_NAME, () => {
  const defaultData = []

  // {
  //     first_name: null,
  //     last_name: null,
  //     email: null,
  //     country_prefix: null,
  //     code: null,
  //     phone: null,
  //     last_usage: null
  // }

  // Очистка старых данных с лишними полями
  const cleanupOldData = () => {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY)
    if (stored) {
      try {
        const data = JSON.parse(stored)
        if (Array.isArray(data) && data.length > 0) {
          // Проверяем, есть ли в данных лишние поля (например, type_of_service, pickup)
          const hasExtraFields = data.some(
            (contact) =>
              contact.type_of_service || contact.pickup || contact.dropoff || contact.hours
          )
          if (hasExtraFields) {
            console.log('Cleaning up old contact data with extra fields')
            localStorage.removeItem(LOCALSTORAGE_KEY)
            return []
          }
        }
        return data
      } catch (e) {
        localStorage.removeItem(LOCALSTORAGE_KEY)
        return []
      }
    }
    return []
  }

  const contactsData = ref(cleanupOldData())
  const selectedContact = ref(null)

  const saveToStorage = () => {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(contactsData.value))
  }

  function select(data) {
    selectedContact.value = data
  }

  function fill(data) {
    contactsData.value = data
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(data))
  }

  function findContact(order) {
    const searchOrder = { ...order }
    if (searchOrder.phone) {
      searchOrder.phone = format_phone(searchOrder.phone, searchOrder.code)
    }
    return contactsData.value.find(
      (x) =>
        x.first_name === searchOrder.first_name &&
        x.last_name === searchOrder.last_name &&
        x.email === searchOrder.email &&
        format_phone(x.phone, x.code) === searchOrder.phone
    )
  }

  function removeDuplicates() {
    const uniqueContacts = []
    const contactSet = new Set()

    contactsData.value.forEach((contact) => {
      const uniqueKey = `${contact.first_name}_${contact.last_name}_${contact.email}_${contact.phone || ''}`
      if (!contactSet.has(uniqueKey)) {
        contactSet.add(uniqueKey)
        uniqueContacts.push(contact)
      }
    })

    contactsData.value = uniqueContacts
    localStorage.setItem('contacts', JSON.stringify(uniqueContacts))
  }

  function findContactByFirstName(first_name) {
    return !!contactsData.value.find((c) => c.first_name === first_name)
  }

  function updateContact(contact_data) {
    if (!contact_data.id && !contact_data.temp_id) {
      return false
    }
    const contactIndex = contactsData.value.findIndex(
      (c) =>
        (contact_data.id && c.id === contact_data.id) ||
        (contact_data.temp_id && c.temp_id === contact_data.temp_id)
    )

    if (contactIndex === -1) {
      return false
    }

    const formatter = new AsYouType()
    formatter.input(contact_data.phone)

    const updatedContact = {
      ...contactsData.value[contactIndex],
      ...contact_data,
      phone: formatter.getNumber()?.number || contact_data.phone,
      code: contact_data.code?.replace?.('+', ''),
      last_usage: new Date().toISOString()
    }

    contactsData.value[contactIndex] = updatedContact
    saveToStorage()
    return true
  }

  function add(contact) {
    if (!contact.first_name || !contact.last_name || !contact.phone) {
      return
    }

    const formattedPhoneValue = format_phone(contact.phone, contact.code)
    // Проверяем, существует ли контакт с таким же телефоном
    const existingContactIndex = contactsData.value.findIndex(
      (c) => format_phone(c.phone, c.code) === formattedPhoneValue
    )

    if (existingContactIndex !== -1) {
      // Если контакт найден, обновляем его last_usage
      contactsData.value[existingContactIndex].last_usage = new Date().toISOString()
    } else {
      // Если контакт не найден, добавляем новый
      const formatter = new AsYouType()
      formatter.input(contact.phone)

      const newContact = {
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        phone: formatter.getNumber()?.number || contact.phone,
        code: contact.code?.replace?.('+', ''),
        country_prefix: contact.country_prefix,
        temp_id: Date.now(), // Присваиваем временный ID
        last_usage: new Date().toISOString()
      }
      contactsData.value.push(newContact)
    }

    saveToStorage()
  }

  function format_phone(phone, code) {
    if (!phone) return ''
    let clearedPhone = phone.replace(/[^0-9]/g, '')
    if (code && clearedPhone.startsWith(code.replace(/[^0-9]/g, ''))) {
      clearedPhone = clearedPhone.substring(code.replace(/[^0-9]/g, '').length)
    }
    return clearedPhone
  }

  function getLastUsage() {
    if (contactsData.value.length == 0) return false

    return contactsData.value.reduce((a, b) => {
      return a.last_usage > b.last_usage ? a : b
    })
  }

  function remove(contact_name) {
    contactsData.value = contactsData.value.filter((c) => c.first_name !== contact_name)
    saveToStorage()
  }

  function $reset() {
    localStorage.removeItem(LOCALSTORAGE_KEY)
  }

  const removeNullPhone = () => {
    if (contactsData.value.length) {
      contactsData.value.map((x, index) => {
        if (x.phone === null) contactsData.value[index].phone = ''
      })
      fill(contactsData.value)
    }
  }

  const loadDefaultValue = () => {
    const localStorageData = localStorage.getItem(LOCALSTORAGE_KEY)

    if (localStorageData) {
      try {
        fill(JSON.parse(localStorageData))
      } catch (e) {
        fill(defaultData)
      }
    } else {
      fill(defaultData)
    }
  }

  loadDefaultValue()
  removeDuplicates()

  return {
    contactsData,
    selectedContact,
    updateContact,
    removeNullPhone,
    format_phone,
    add,
    remove,
    fill,
    getLastUsage,
    select,
    findContact,
    removeDuplicates,
    $reset,
    findContactByFirstName
  }
})
